/**
 * Shared workspaces (Deel B): reactive views over the locally synced
 * workspaces/workspace_members tables, plus the small bit of UI state
 * (which dialog is open) that doesn't belong in local storage.
 */
import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { buildTree, type PageNode, type PageRecord } from '@nexus/store';
import { useStoreQuery } from '../db/context';
import { serverConfigured } from '../sync/config';
import { supabase, useSyncState } from '../sync/useSync';

export interface WorkspaceSummary {
  id: string;
  name: string;
  createdBy: string;
  /** The signed-in user's own permission in this workspace. */
  permission: 'read' | 'write';
  /** 'pending' = invited but not yet responded to; doesn't show in the sidebar tree yet. */
  status: 'pending' | 'active';
}

/** The signed-in user's id, or null while loading / signed out / no server set up. */
export function useOwnUserId(): string | null {
  const [id, setId] = useState<string | null>(null);
  useEffect(() => {
    if (!serverConfigured()) return;
    let cancelled = false;
    void supabase()
      .auth.getUser()
      .then(({ data }) => {
        if (!cancelled) setId(data.user?.id ?? null);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return id;
}

/** Every workspace membership the signed-in user has, any status. */
function useMemberships(): WorkspaceSummary[] {
  const userId = useOwnUserId();
  return (
    useStoreQuery(
      async (s) => {
        if (!userId) return [];
        const workspaces = await s.listWorkspaces();
        const out: WorkspaceSummary[] = [];
        for (const w of workspaces) {
          const members = await s.listWorkspaceMembers(w.id);
          const mine = members.find((m) => m.userId === userId);
          if (mine)
            out.push({
              id: w.id,
              name: w.name,
              createdBy: w.createdBy,
              permission: mine.permission,
              status: mine.status,
            });
        }
        return out;
      },
      [userId],
      ['workspaces'],
    ) ?? []
  );
}

/** Workspaces the signed-in user is an active member of, with their own permission. */
export function useWorkspaces(): WorkspaceSummary[] {
  return useMemberships().filter((w) => w.status === 'active');
}

/** Invites awaiting the signed-in user's response. */
export function usePendingInvites(): WorkspaceSummary[] {
  return useMemberships().filter((w) => w.status === 'pending');
}

/** Names (display name, else email) of a workspace's members, cached per workspace. */
interface MemberNamesState {
  byWorkspace: Record<string, Record<string, string>>;
}
const useMemberNamesStore = create<MemberNamesState>(() => ({ byWorkspace: {} }));

export function useMemberNames(workspaceId: string | null): Record<string, string> {
  const cached = useMemberNamesStore((s) => (workspaceId ? s.byWorkspace[workspaceId] : undefined));
  useEffect(() => {
    if (!workspaceId || cached) return;
    void supabase()
      .functions.invoke<{
        members?: { userId: string; email: string | null; displayName: string | null }[];
      }>('workspace-members', { body: { action: 'list-members', workspaceId } })
      .then(({ data }) => {
        const map: Record<string, string> = {};
        for (const m of data?.members ?? []) map[m.userId] = m.displayName ?? m.email ?? m.userId;
        useMemberNamesStore.setState((s) => ({
          byWorkspace: { ...s.byWorkspace, [workspaceId]: map },
        }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);
  return cached ?? {};
}

/** A single workspace's own permission lookup (for a page already known to be in it). */
export function useWorkspacePermission(workspaceId: string | null): 'read' | 'write' | null {
  const workspaces = useWorkspaces();
  if (!workspaceId) return null;
  return workspaces.find((w) => w.id === workspaceId)?.permission ?? null;
}

/** A workspace's own page tree (pages with that `workspace_id`), kept live. */
export function useWorkspacePages(
  workspaceId: string,
): { list: PageRecord[]; tree: PageNode[] } | undefined {
  return useStoreQuery(
    async (s) => {
      const list = await s.listPages({ workspaceId });
      return { list, tree: buildTree(list) };
    },
    [workspaceId],
    ['pages'],
  );
}

/** Which workspace dialog, if any, is open. */
interface WorkspaceUiState {
  createOpen: boolean;
  manageId: string | null;
  openCreate: () => void;
  openManage: (workspaceId: string) => void;
  close: () => void;
}

export const useWorkspaceUi = create<WorkspaceUiState>((set) => ({
  createOpen: false,
  manageId: null,
  openCreate: () => set({ createOpen: true }),
  openManage: (workspaceId) => set({ manageId: workspaceId }),
  close: () => set({ createOpen: false, manageId: null }),
}));

/** Pull workspace metadata/membership (and any newly visible pages) right away. */
export function refreshWorkspacesSoon(): void {
  void useSyncState
    .getState()
    .engine?.sync()
    .catch(() => {});
}
